import React, {
    useState,
    useEffect,
    useMemo,
    useReducer,
    useCallback,
    useContext,
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ErrorAlert, TitleWrapper, Title, AnimatedLoader } from 'smartsuite-ui';
import WidgetPrintView from './WidgetPrintView';
import SsiHeader from '../../../customizations/smartSort/SsiHeader';
import SsiFooter from '../../../customizations/smartSort/SsiFooter';
import PrintViewHeader from '../PrintViewHeader';
import {
    getRequiredFiltersWithOneOption,
    isLandscape,
} from '../../dashboardUtils';
import { getLegendFormatter } from '../../widgets/utils/dataFormatUtils';
import dashboardReducer from '../../stateManagement/dashboardReducer';
import DashboardContext from '../../../shared/hooks/dashboardContext';
import { callData } from '../../utils/metricRequestUtils';
import { removeWebClientHeader } from '../exportUtils';

function addPrintOrientationStyles(landscape) {
    const styles = landscape
        ? '@page { size: A4 landscape; margin: 0; }'
        : '@page { size: A4 portrait; margin: 0; }';

    const printOrientationStyles = document.createElement('style');
    printOrientationStyles.type = 'text/css';

    if (printOrientationStyles.styleSheet)
        printOrientationStyles.styleSheet.cssText = styles;
    else printOrientationStyles.appendChild(document.createTextNode(styles));

    document.querySelector('head').appendChild(printOrientationStyles);
}

function areAllChartsLoaded(handle, setAllChartsLoaded, metricData) {
    if (_.isEmpty(metricData)) {
        return;
    }

    const charts = document.querySelectorAll('.recharts-wrapper');
    const chartsArray = Array.prototype.slice.call(charts);
    const allLoaded = chartsArray.every((chart) => chart.clientWidth !== 0);

    if (allLoaded) {
        clearInterval(handle);
        setAllChartsLoaded(true);
    }
}

/* eslint-disable max-statements, complexity */
export default function DashboardPrintView(props) {
    const { viewConfiguration } = props;

    const { selectOptions, uriConstants } = useContext(DashboardContext);

    const [allChartsLoaded, setAllChartsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const handleError = useCallback(
        (errorMessage) => setError(errorMessage),
        []
    );

    const [state, dispatch] = useReducer(dashboardReducer, { metricData: [] });

    const dashboardRecordLimit = useMemo(
        () => ({
            recordLimit: viewConfiguration.recordLimit,
            isDescending: !!viewConfiguration.isDescending,
        }),
        [viewConfiguration]
    );

    const dashboardFilterValues = useMemo(
        () => ({
            ...getRequiredFiltersWithOneOption(
                viewConfiguration.filterConfigurations,
                viewConfiguration.requiredFilterKeys,
                selectOptions
            ),
            ...viewConfiguration.filterValues,
        }),
        [selectOptions, viewConfiguration]
    );

    useEffect(() => {
        const filterState = {
            timeAndPrecision: viewConfiguration.timeAndPrecision,
            filterValues: dashboardFilterValues,
            whereClauses: viewConfiguration.whereClauses,
            recordLimit: dashboardRecordLimit,
        };
        callData(
            props.dashboardKey,
            filterState,
            dispatch,
            uriConstants.metricDataUri,
            viewConfiguration.viewKey,
            viewConfiguration.widgets,
            {},
            handleError
        );
    }, [
        props.dashboardKey,
        dashboardFilterValues,
        dashboardRecordLimit,
        handleError,
        selectOptions,
        uriConstants.metricDataUri,
        viewConfiguration,
    ]);

    useEffect(() => {
        document.title = viewConfiguration.title;
        removeWebClientHeader();
        addPrintOrientationStyles(isLandscape(props.printViewMode));
    }, [viewConfiguration, props.printViewMode]);

    useEffect(() => {
        const handle = setInterval(
            () =>
                areAllChartsLoaded(
                    handle,
                    setAllChartsLoaded,
                    state.metricData
                ),
            1000
        );

        return () => setAllChartsLoaded(false);
    }, [state.metricData]);

    useEffect(() => {
        if (allChartsLoaded) {
            window.print();
        }
    }, [allChartsLoaded]);

    function renderWidgets(widgets, panelId) {
        return widgets.map((widget, index) => (
            <WidgetPrintView
                // eslint-disable-next-line react/no-array-index-key
                key={`${panelId}-${index}`}
                defaultFilterValues={dashboardFilterValues}
                defaultTimeAndPrecision={viewConfiguration.timeAndPrecision}
                defaultWhereClauses={viewConfiguration.whereClauses}
                defaultRecordLimit={dashboardRecordLimit}
                friendlyColumnLabels={viewConfiguration.friendlyColumnLabels}
                metrics={state.metricData}
                printViewMode={props.printViewMode}
                widgetConfiguration={widget}
            />
        ));
    }

    function getPanelClassName(printSettings = {}) {
        let panelClassName = 'print-grid-panel';

        if (isLandscape(props.printViewMode)) {
            if (printSettings.isFullWidth) {
                panelClassName += ' print-grid-panel--full-width';
            }

            if (printSettings.isCentered) {
                panelClassName += ' print-grid-panel--centered';
            }
        }

        return panelClassName;
    }

    function renderPanelTabs(panel, panelWidgets) {
        if (!panel.tabs) {
            return (
                <div
                    key={panel.id}
                    className={getPanelClassName(panel.printSettings)}
                >
                    {panel.header && (
                        <Title cssClassModifiers={['size-medium']}>
                            {panel.header}
                        </Title>
                    )}
                    {renderWidgets(panelWidgets, panel.id)}
                </div>
            );
        }
        const groupedPanelWidgets = _.groupBy(panelWidgets, (x) => x.tabId);
        const groupedPanelKeys = Object.keys(groupedPanelWidgets);

        const panelHeader = panel.header ? `${panel.header} - ` : '';

        return groupedPanelKeys.map((tabId) => (
            <div key={tabId} className={getPanelClassName(panel.printSettings)}>
                <Title cssClassModifiers={['size-medium']}>
                    {`${panelHeader}${
                        panel.tabs.find((tab) => tab.id?.toString() === tabId)
                            .label
                    }`}
                </Title>
                {renderWidgets(groupedPanelWidgets[tabId], panel.id)}
            </div>
        ));
    }

    function renderPanels() {
        return viewConfiguration.panels.map((panel) => {
            const panelWidgets = viewConfiguration.widgets.reduce(
                (result, widget) => {
                    if (widget.panelId !== panel.id) {
                        return result;
                    }

                    result.push(widget);
                    return result;
                },
                []
            );

            return renderPanelTabs(panel, panelWidgets);
        });
    }

    function renderLoadingOverlay() {
        if (allChartsLoaded) {
            return null;
        }

        return (
            <div className="dashboard-print-view-overlay">
                <div className="dashboard-print-view-overlay__background" />
                <AnimatedLoader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-print-view">
                <TitleWrapper title={viewConfiguration.title} />
                <ErrorAlert>
                    {`Error: ${error.exceptionMessage || error}`}
                </ErrorAlert>
            </div>
        );
    }

    const filterLabels = _.reduce(
        viewConfiguration.filterConfigurations,
        (result, filterValue, filterKey) => ({
            ...result,
            [filterKey]: filterValue?.label,
        }),
        viewConfiguration.friendlyColumnLabels
    );

    return (
        <div
            className={`dashboard-print-view dashboard-print-view--${props.printViewMode.toLowerCase()}`}
        >
            <PrintViewHeader
                filterFormatter={getLegendFormatter([], filterLabels)}
                filterValues={dashboardFilterValues}
                timeAndPrecision={viewConfiguration.timeAndPrecision}
                subTitle={viewConfiguration.subTitle}
                title={viewConfiguration.title}
                whereClauses={viewConfiguration.whereClauses}
            />
            {viewConfiguration.showSsiWarning && <SsiHeader />}
            <div className="dashboard-widgets">{renderPanels()}</div>
            {viewConfiguration.showSsiWarning && <SsiFooter />}
            {renderLoadingOverlay()}
        </div>
    );
}
/* eslint-enable max-statements, complexity */

DashboardPrintView.propTypes = {
    viewConfiguration: PropTypes.instanceOf(Object).isRequired,
    dashboardKey: PropTypes.string.isRequired,
    printViewMode: PropTypes.string.isRequired,
};
